import {onAjaxError} from '@Utils/ajax.util';

const THUNK_REQUEST_STATUS_REJECTED = 'rejected';

/**
 * Executes the thunk callback that dispatches a redux thunk and does error handling.
 * The error callback is executed if there is an error in the returned promise, or if the request status is 'rejected'
 * for a thunk that actually ran — redux thunks execute a 'rejected' promise on failure
 * When no error callback is specified, the default opens the error modal with the rejection's own message
 * A thunk skipped by its own `condition` (RTK rejects it with a ConditionError) is neither a success nor a failure:
 * the work was deliberately not started, usually because an earlier dispatch already loaded the data, so no callback runs
 * @param thunkDispatchCallback Function that dispatches a redux thunk
 * @param onSuccessCallback Callback function to be executed on success
 * @param onErrorCallback Callback function to be executed on error
 */
export const executeThunk = async (thunkDispatchCallback: any, onSuccessCallback: any = null, onErrorCallback: any = null): Promise<void> => {
  const response = await thunkDispatchCallback();
  if (wasThunkSkippedByCondition(response)) {
    return;
  }
  if (hasThunkRequestFailed(response)) {
    if (isValidCallback(onErrorCallback)) {
      onErrorCallback!(response);
    } else {
      onAjaxError(response.error, response.error.message, false);
    }
  } else if (isValidCallback(onSuccessCallback)) {
    onSuccessCallback!(response.payload);
  }
};

/**
 * Checks if the redux thunk request has failed
 * @param response Response object from thunk dispatch
 * @returns {boolean}
 */
const hasThunkRequestFailed = (response: any): boolean => {
  return response.error || response.meta?.requestStatus === THUNK_REQUEST_STATUS_REJECTED;
};

/**
 * Checks if the thunk never ran because its `condition` option returned false: createAsyncThunk marks that rejected action with meta.condition,
 * and only that one — a fulfilled action may carry the same key via fulfillWithValue meta
 * @param response Response object from thunk dispatch
 * @returns {boolean}
 */
const wasThunkSkippedByCondition = (response: unknown): boolean => {
  const meta = (response as {meta?: {condition?: boolean; requestStatus?: string}} | undefined)?.meta;
  return meta?.condition === true && meta.requestStatus === THUNK_REQUEST_STATUS_REJECTED;
};

/**
 * Checks if a callback function is valid
 * @param callback Callback function to be checked
 * @returns {boolean}
 */
const isValidCallback = (callback: any | null): boolean => {
  return !!(callback && typeof callback === 'function');
};
